import React, { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { Paper, TableCell } from '@mui/material'
import { TableActions } from '../table-actions'
import { USERS_TABLE_ACTIONS, USERS_TABLE_COLUMNS } from '../../constants'
import { marketerUsersTableStyles } from './marketer-users-table.styles'
import { TableUrlPagination } from '../../../../ui'

const getLookup = (combo) => {
  let lookup = {}
  combo.forEach((item) => (lookup[item.key] = item.value))
  return lookup
}

export const MarketerUsersTable = () => {
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const {
    data,
    search,
    pagination: { total_usuarios: totalEntries },
    isLoading,
  } = useSelector((state) => state.marketerUsers)
  const combos = useSelector((state) => state.combos)

  const handleRowClick = useCallback(
    (e, row) => {
      e.stopPropagation()
      navigate(`/marketerUsers/${row['id']}`)
    },
    [navigate]
  )

  const formattedColumns = useMemo(
    () =>
      [...USERS_TABLE_COLUMNS, ...USERS_TABLE_ACTIONS].map((column) => {
        // Base column configuration for Material React Table
        const baseColumn = {
          accessorKey: column.field,
          id: column.field,
          size: column.width || 200,
          minSize: column.minWidth || 100,
          maxSize: column.maxWidth,
          enableSorting: column.sorting !== false,
          muiTableHeadCellProps: {
            align: column.align || 'left',
          },
          muiTableBodyCellProps: {
            align: column.align || 'left',
            sx: {
              minWidth: column.minWidth || 100,
            },
          },
        }

        // Handle action column (active field)
        if (column.field === 'active') {
          return {
            ...baseColumn,
            disableClick: true,
            header: formatMessage({ id: `global.${column.field}` }),
            Cell: ({ row }) => <TableActions row={row} />,
          }
        }

        // Handle regular columns
        const result = {
          ...baseColumn,
          header: formatMessage({ id: `pages.marketer.users.table.columns.${column.field}.title` }),
        }

        // Add lookup functionality if combo data exists
        if (column.hasOwnProperty('comboId') && combos[column.comboId] && combos[column.comboId].data) {
          const lookup = getLookup(combos[column.comboId].data)
          result.Cell = ({ cell }) => {
            const value = cell.getValue()
            const lookupValue = lookup[value] || value
            return lookupValue ? <span>{lookupValue}</span> : <span>--</span>
          }
        } else {
          // Default cell renderer with empty value handling
          result.Cell = ({ cell }) => {
            const value = cell.getValue()
            if (value === null || value === undefined || value === '') {
              return <span>--</span>
            }
            return <span>{value}</span>
          }
        }

        return result
      }),
    [combos, formatMessage] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const processedData = useMemo(
    () =>
      data.map((item) => {
        return {
          ...item,
          tableData: {
            ...item.tableData,
          },
        }
      }),
    [data]
  )

  const tableOptions = useMemo(
    () => ({
      toolbar: false,
      selection: false,
      fixedColumns: {
        right: 1,
      },
      tableLayout: 'fixed',
      draggable: false,
    }),
    []
  )

  const tableComponents = useMemo(
    () => ({
      Container: ({ children }) => <Paper elevation={0}>{children}</Paper>,
      Cell: (props) => (
        <TableCell {...props} style={{ padding: 0 }}>
          {props.columnDef.field === 'active' ? <TableActions {...props} /> : props.children}
        </TableCell>
      ),
    }),
    []
  )

  return (
    <TableUrlPagination
      columns={formattedColumns}
      options={tableOptions}
      totalEntries={totalEntries}
      search={search}
      components={tableComponents}
      //Not transformed in the url table interface:
      data={processedData}
      isLoading={isLoading}
      onRowClick={handleRowClick}
    />
  )
}
